/**
 * Document types — mirrors the taxhive-document-service API contract
 * (`DocumentResponse` / `DocumentList` schemas), camelCased for the UI.
 *
 * Field names follow the API SCHEMA (`name`, `fileType`, `linkedEntityType`),
 * not the raw DB model (`document_name`, `entity_type`). The HTTP mappers do
 * the snake⇄camel conversion.
 */

// ============================================================================
// ENUM-LIKE UNIONS
// ============================================================================

/** Document lifecycle status. Matches `DocumentStatus` on the backend. */
export type DocumentStatus =
  | 'pending'
  | 'uploaded'
  | 'processing'
  | 'verified'
  | 'rejected'
  | 'archived'

/**
 * Document category. The backend enum has ~70 values; the UI groups the common
 * ones used for filtering. The union is intentionally a string-superset so an
 * unrecognised backend category still type-checks (`string & {}`).
 */
export type DocumentCategory =
  | 'pan_card'
  | 'aadhaar_card'
  | 'passport'
  | 'form_16'
  | 'form_26as'
  | 'salary_slip'
  | 'bank_statement'
  | 'investment_proof'
  | 'itr_acknowledgement'
  | 'itr_filed_copy'
  | 'gst_return_copy'
  | 'audit_report'
  | 'balance_sheet'
  | 'property_deed'
  | 'capital_gain_statement'
  | 'demand_notice'
  | 'photograph'
  | 'cancelled_cheque'
  | 'other'
  // eslint-disable-next-line @typescript-eslint/ban-types
  | (string & {})

/** Access-control level. Matches `DocumentAccessLevel`. */
export type DocumentAccessLevel = 'public' | 'team' | 'private' | 'client'
export type DocumentWorkspaceService = 'income_tax' | 'gst' | 'finance'

/**
 * Linked-entity type. The backend stores a free-form string (e.g.
 * `customer`, `individual_customer`, `business_customer`). The UI passes
 * `customer` for the customer Documents tab.
 */
export type DocumentEntityType =
  | 'customer'
  | 'individual_customer'
  | 'business_customer'
  | 'assessment'
  // eslint-disable-next-line @typescript-eslint/ban-types
  | (string & {})

// ============================================================================
// MAIN DOCUMENT INTERFACE
// ============================================================================

export interface Document {
  id: string

  // Identity
  name: string
  description?: string

  // File
  filePath: string
  fileSize: number // bytes
  fileType: string // extension, e.g. 'pdf'
  fileHash?: string
  mimeType?: string
  fileSizeMb?: number

  // Categorisation
  category: DocumentCategory
  status: DocumentStatus
  accessLevel?: DocumentAccessLevel
  assessmentYear?: string
  financialYear?: string
  workspaceService?: DocumentWorkspaceService
  tags?: string[]

  // Linking
  linkedEntityType?: DocumentEntityType
  linkedEntityId?: string

  // Validity
  validFrom?: string
  validUntil?: string
  isExpired?: boolean

  // Verification
  isVerified: boolean
  verifiedBy?: string
  verifiedByName?: string
  verifiedAt?: string
  verificationNotes?: string

  // Versioning
  parentDocumentId?: string
  version: number
  isLatest: boolean

  // Audit
  createdBy?: string
  createdByName?: string
  updatedBy?: string
  updatedByName?: string
  createdAt: string
  updatedAt: string
}

/** A single version row from `GET /documents/{id}/versions`. */
export interface DocumentVersion {
  id: string
  version: number
  isLatest: boolean
  parentDocumentId?: string
  fileHash?: string
  fileSize: number
  createdBy?: string
  createdByName?: string
  createdAt: string
}

export type EvidenceVersionState = 'UPLOADING' | 'SCANNING' | 'QUARANTINED' | 'AVAILABLE' | 'REJECTED' | 'SUPERSEDED'

export interface EvidenceDocumentVersion {
  id: string
  document_id: string
  version_number: number
  replaces_version_id?: string | null
  state: EvidenceVersionState
  original_filename: string
  file_extension: string
  declared_mime: string
  detected_mime?: string | null
  expected_size: number
  byte_size?: number | null
  expected_sha256: string
  content_sha256?: string | null
  integrity_verified: boolean
  content_type_verified: boolean
  rejection_code?: string | null
  quarantine_code?: string | null
  created_at: string
  completed_at?: string | null
}

export interface EvidenceUploadResult {
  document_id: string
  version: EvidenceDocumentVersion
  completion_path: string
}

export interface EvidenceAccessGrant {
  document_id: string
  version_id: string
  case_id: string
  action: 'PREVIEW' | 'DOWNLOAD'
  expires_at: string
  access_url: string
  page?: number | null
  preview_available?: boolean | null
  accessible_fallback?: string | null
}

// ============================================================================
// INPUTS
// ============================================================================

/**
 * Upload input. In real mode the `file` is sent as multipart; the rest are
 * query params. In mock mode the binary is ignored — only metadata is used.
 */
export interface UploadDocumentInput {
  file?: File
  name?: string
  category: DocumentCategory
  assessmentYear?: string
  financialYear?: string
  workspaceService?: DocumentWorkspaceService
  description?: string
  linkedEntityType?: DocumentEntityType
  linkedEntityId?: string
  parentDocumentId?: string
  version?: number
}

export interface DocumentListParams {
  page?: number
  pageSize?: number
  status?: DocumentStatus
  category?: DocumentCategory
  fileType?: string
  linkedEntityId?: string
  assessmentYear?: string
}

// ============================================================================
// FILTER / UI STATE
// ============================================================================

export interface DocumentFilters {
  category?: DocumentCategory | 'all'
  status?: DocumentStatus | 'all'
  assessmentYear?: string | 'all'
  /** ISO date — only documents uploaded on/after this date. */
  uploadedAfter?: string
  /** ISO date — only documents uploaded on/before this date. */
  uploadedBefore?: string
}

export const ASSESSMENT_YEAR_OPTIONS = [
  '2026-27',
  '2025-26',
  '2024-25',
  '2023-24',
  '2022-23',
] as const

// ============================================================================
// DISPLAY LABELS
// ============================================================================

export const DOCUMENT_STATUS_LABEL: Record<DocumentStatus, string> = {
  pending: 'Pending',
  uploaded: 'Uploaded',
  processing: 'Processing',
  verified: 'Verified',
  rejected: 'Rejected',
  archived: 'Archived',
}

/** Human label for a category code. Falls back to a Title-Cased version. */
export function labelForCategory(category: DocumentCategory): string {
  const KNOWN: Record<string, string> = {
    pan_card: 'PAN Card',
    aadhaar_card: 'Aadhaar Card',
    passport: 'Passport',
    form_16: 'Form 16',
    form_26as: 'Form 26AS',
    salary_slip: 'Salary Slip',
    bank_statement: 'Bank Statement',
    investment_proof: 'Investment Proof',
    itr_acknowledgement: 'ITR Acknowledgement',
    itr_filed_copy: 'ITR Filed Copy',
    gst_return_copy: 'GST Return Copy',
    audit_report: 'Audit Report',
    balance_sheet: 'Balance Sheet',
    property_deed: 'Property Deed',
    capital_gain_statement: 'Capital Gain Statement',
    demand_notice: 'Demand Notice',
    photograph: 'Photograph',
    cancelled_cheque: 'Cancelled Cheque',
    other: 'Other',
  }
  if (KNOWN[category]) return KNOWN[category]
  return category
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

/** Categories surfaced in the filter dropdown (common subset). */
export const DOCUMENT_CATEGORY_OPTIONS: DocumentCategory[] = [
  'pan_card',
  'aadhaar_card',
  'form_16',
  'form_26as',
  'salary_slip',
  'bank_statement',
  'investment_proof',
  'itr_acknowledgement',
  'itr_filed_copy',
  'gst_return_copy',
  'audit_report',
  'balance_sheet',
  'property_deed',
  'capital_gain_statement',
  'demand_notice',
  'other',
]

/** Format a byte count into a human-readable string. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
